import { CommonModule } from '@angular/common';
import { Component, computed, input, Signal, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { GalaxyPopoverModule } from '@vendasta/galaxy/popover';
import { GalaxySnackbarModule, SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MessagingStatus } from '@vendasta/instagram';
import { GetMessagingStatusResponseInterface } from '@vendasta/instagram/lib/_internal/interfaces/messaging.interface';
import { BehaviorSubject, catchError, combineLatest, firstValueFrom, map, of, startWith, switchMap } from 'rxjs';
import { SocialPageInstagram } from '../interfaces';
import { InstagramMessagesService, InstagramPageConnectionInfo } from './instagram-messages.service';

type badgeValue = {
  color: 'red' | 'green';
  i18nKey: string;
};

type displayedButton = 'sign-in' | 'enable' | 'disable' | null;

@Component({
  selector: 'inbox-instagram-messages',
  imports: [
    CommonModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule,
    GalaxyBadgeModule,
    MatProgressSpinnerModule,
    GalaxyAlertModule,
    GalaxyPopoverModule,
    GalaxySnackbarModule,
  ],
  templateUrl: './instagram-messages.component.html',
  styleUrl: './instagram-messages.component.scss',
})
export class InstagramMessagesComponent {
  readonly loadingMessagingStatus: Signal<boolean>;
  readonly loadingAccounts: Signal<boolean>;
  readonly loadingEnableDisableRequest = signal<boolean>(false);
  readonly loading = computed(
    () => this.loadingMessagingStatus() || this.loadingAccounts() || this.loadingEnableDisableRequest(),
  );

  readonly pageName: Signal<string>;
  readonly pageUrl: Signal<string>;
  readonly badgeValue: Signal<badgeValue | null>;
  readonly manyPages: Signal<boolean>;
  // TODO: Probably remove noPages
  readonly noPages: Signal<boolean>;
  readonly buttonToDisplay: Signal<displayedButton>;
  readonly authTokenMissingPermissions: Signal<boolean>;
  readonly unknownError: Signal<boolean>;
  // user does not have required auth to view connection status, connect, or disconnect
  readonly authError: Signal<boolean>;
  readonly messengerEnabled: Signal<boolean>;

  private readonly connectedPages: Signal<SocialPageInstagram[]>;
  private readonly pageInfo: Signal<InstagramPageConnectionInfo | null>;
  private readonly messagingStatus: Signal<GetMessagingStatusResponseInterface | null>;

  protected readonly MessagingStatus = MessagingStatus;
  protected readonly status = computed(() => this.messagingStatus()?.status);

  private readonly refreshMessagingStatus$ = new BehaviorSubject(null);

  accountID = input.required<string>();
  partnerID = input.required<string>();

  constructor(
    private readonly instagramMessagesService: InstagramMessagesService,
    private readonly snackbarService: SnackbarService,
    private readonly route: ActivatedRoute,
  ) {
    this.manyPages = computed(() => {
      return this.connectedPages().length > 1;
    });

    this.noPages = computed(() => {
      return this.connectedPages().length === 0;
    });

    const instagramUserIdFromQueryParam$ = this.route.queryParamMap.pipe(map((m) => m.get('instagramUserId')));
    const instagramUserIdFromReAuthQueryParam$ = this.route.queryParamMap.pipe(map((m) => m.get('message')));
    const accountId$ = toObservable(this.accountID);

    const connectedPages$ = combineLatest([
      accountId$,
      instagramUserIdFromQueryParam$,
      instagramUserIdFromReAuthQueryParam$,
    ]).pipe(
      switchMap(([accountID, instagramUserId, reauthInstagramUserId]) => {
        if (instagramUserId || reauthInstagramUserId) {
          const id: string = instagramUserId || reauthInstagramUserId || '';
          return this.instagramMessagesService.getInstagramUser(accountID, id).pipe(map((user) => [user]));
        }
        return this.instagramMessagesService.listInstagramUsers(accountID);
      }),
      map((r) => ({ loading: false, result: r })),
      startWith({ loading: true, result: [] }),
      catchError(() => of({ loading: false, result: [] })),
    );

    this.loadingAccounts = toSignal(connectedPages$.pipe(map((r) => r.loading)), { initialValue: true });
    this.connectedPages = toSignal(connectedPages$.pipe(map((r) => r.result)), {
      initialValue: [],
    });

    const messagingStatus$ = combineLatest([
      toObservable(this.partnerID),
      toObservable(this.accountID),
      instagramUserIdFromQueryParam$,
      this.refreshMessagingStatus$,
    ]).pipe(
      switchMap(([partnerID, accountID, instagramUserId]) => {
        return this.instagramMessagesService.getMessagingStatus(partnerID, accountID).pipe(
          map((r) => {
            if (instagramUserId && r.status === MessagingStatus.USER_NOT_CONNECTED) {
              return { ...r, status: MessagingStatus.MESSAGING_CAN_BE_ENABLED };
            }
            return r;
          }),
          map((r) => ({ loading: false, status: r })),
          startWith({ loading: true, status: null }),
          catchError(() => of({ loading: false, status: null })),
        );
      }),
    );
    this.loadingMessagingStatus = toSignal(messagingStatus$.pipe(map((r) => r.loading)), { initialValue: true });
    this.messagingStatus = toSignal(messagingStatus$.pipe(map((r) => r.status)), {
      initialValue: null,
    });

    this.pageInfo = computed(() => {
      const messagingStatus = this.messagingStatus();
      const connectedPages = this.connectedPages();
      if (messagingStatus === null || connectedPages.length === 0) return null;
      const connectedPage =
        connectedPages.find((connectedPage) => connectedPage.instagramUserId === messagingStatus?.instagramUserId) ??
        connectedPages[0];

      return {
        name: connectedPage?.name ?? '',
        internalInstagramUserId: connectedPage.internalInstagramUserId,
        profileUrl: connectedPage?.profileUrl,
        status: messagingStatus?.status ?? MessagingStatus.UNKNOWN_STATUS,
        statusCheckPermissionError: false,
      };
    });

    this.badgeValue = computed(() => {
      return {
        color: this.messengerEnabled() ? 'green' : 'red',
        i18nKey: this.messengerEnabled() ? 'INBOX.SETTINGS.ACTIVE' : 'INBOX.SETTINGS.INACTIVE',
      };
    });
    this.pageName = computed(() => this.pageInfo()?.name ?? '');
    this.pageUrl = computed(() => this.pageInfo()?.profileUrl ?? '');

    this.authError = computed(() => this.pageInfo()?.statusCheckPermissionError ?? false);

    this.messengerEnabled = computed(() => this.pageInfo()?.status === MessagingStatus.MESSAGING_ENABLED);
    this.unknownError = computed(() => this.pageInfo()?.status === MessagingStatus.UNKNOWN_STATUS);
    this.authTokenMissingPermissions = computed(() => {
      const status = this.pageInfo()?.status;
      return (
        status === MessagingStatus.MESSAGING_TOKEN_MISSING_PERMISSIONS ||
        status === MessagingStatus.USER_TOKEN_MISSING_PERMISSIONS
      );
    });

    this.buttonToDisplay = computed(() => {
      switch (this.status()) {
        case MessagingStatus.MESSAGING_CAN_BE_ENABLED:
          return 'enable';
        case MessagingStatus.MESSAGING_ENABLED:
          return 'disable';
        case MessagingStatus.USER_NOT_CONNECTED:
          return 'sign-in';
        default:
          return null;
      }
    });
  }

  async authClick(reAuth = false) {
    const internalInstagramUserId = this.pageInfo()?.internalInstagramUserId;
    if (reAuth && internalInstagramUserId) {
      return this.instagramMessagesService.redirectToFacebookReAuth(this.accountID(), internalInstagramUserId);
    }
    return this.instagramMessagesService.redirectToFacebookAuth(this.accountID());
  }

  async connect(): Promise<void> {
    this.loadingEnableDisableRequest.set(true);
    return firstValueFrom(this.instagramMessagesService.enableMessaging(this.connectedPages()[0]))
      .then(() => {
        this.snackbarService.openSuccessSnack('INBOX.SETTINGS.INSTAGRAM_MESSAGES.SUCCESS.SUCCESSFULLY_ENABLED');
        this.refreshMessagingStatus$.next(null);
      })
      .catch(() => {
        this.snackbarService.openErrorSnack('INBOX.SETTINGS.INSTAGRAM_MESSAGES.ERRORS.FAILED_TO_ENABLE');
      })
      .finally(() => this.loadingEnableDisableRequest.set(false));
  }

  async disconnect(): Promise<void> {
    const instagramUserId = this.messagingStatus()?.instagramUserId;
    if (!instagramUserId) return Promise.reject('Cannot disconnect when not connected');
    this.loadingEnableDisableRequest.set(true);
    return firstValueFrom(this.instagramMessagesService.disableMessaging(instagramUserId))
      .then(() => {
        this.snackbarService.openSuccessSnack('INBOX.SETTINGS.FACEBOOK_MESSENGER.SUCCESS.SUCCESSFULLY_DISABLED');
        this.refreshMessagingStatus$.next(null);
      })
      .catch(() => {
        this.snackbarService.openErrorSnack('INBOX.SETTINGS.FACEBOOK_MESSENGER.ERRORS.FAILED_TO_DISABLE');
      })
      .finally(() => this.loadingEnableDisableRequest.set(false));
  }
}
